// Basic
import { useCallback, useRef, useState } from 'react';
// Libs
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
// Store
import useCustomPayStore from '@/app/store/customPay';
// Components
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/common-utils/components/ui/accordion';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Sp2525Newsletter180625 from './oldNews/Sp2525Newsletter180625';
import Sp2531Newsletter230725 from './oldNews/Sp2531Newsletter230725';
import Sp2537Newsletter090925 from './oldNews/Sp2537Newsletter090925';
import Sp2540Newsletter300925 from './oldNews/Sp2540Newsletter300925';
import Sp2549Newsletter021225 from './oldNews/Sp2549Newsletter021225';
import Sp2603Newsletter060126 from './oldNews/Sp2603Newsletter060126';
import Sp2627Newsletter300626 from './Sp2627Newsletter300626';
// Common
import ICONS from '@/common-utils/enums/icons';
// Lang
import { nsNewsletter } from '@/app/i18n/namespaces';
import Icon from '@/common-utils/components/renders/icon/Icon';

/**
 * @returns {JSX.Element} NewsLetterContent
 */
function NewsLetterContent() {

    // Constants
    //  Add new newsletters here, sorted by date
    const NEWSLETTERS = [
        { key: 'sp2627', date: '2026-06-30', Component: Sp2627Newsletter300626, hideForCustomPay: true },
        { key: 'sp2603', date: '2026-01-06', Component: Sp2603Newsletter060126 },
        { key: 'sp2549', date: '2025-12-02', Component: Sp2549Newsletter021225, hideForCustomPay: true },
        { key: 'sp2540', date: '2025-09-30', Component: Sp2540Newsletter300925 },
        { key: 'sp2537', date: '2025-09-09', Component: Sp2537Newsletter090925 },
        { key: 'sp2531', date: '2025-07-23', Component: Sp2531Newsletter230725, hideForCustomPay: true },
        { key: 'sp2525', date: '2025-06-18', Component: Sp2525Newsletter180625 }
    ];

    // Store
    const isCustomPay = useCustomPayStore((s) => s.isCustomPay);

    // Hooks
    const imageLoadedRef = useRef(1);
    const { i18n } = useTranslation();

    // Constants
    const visibleNewsletters = NEWSLETTERS.filter(n => !n.hideForCustomPay || !isCustomPay);
    const DATE_FORMAT = 'dd/MM/yyyy';

    // State
    const [openItems, setOpenItems] = useState([visibleNewsletters[0]?.key]);

    // Functions
    const handleImageLoad = useCallback(() => {
        imageLoadedRef.current += 1;
    }, []);

    // Constants
    const baseNewsletterProps = {
        isCustomPay,
        handleImageLoad
    };

    return (
        <div>
            <Accordion type='multiple' value={openItems} onValueChange={setOpenItems}>
                {visibleNewsletters.map((newsletter, index) => {
                    const isFirst = index === 0;
                    const formattedDate = DateTime.fromISO(newsletter.date).setLocale(i18n.language).toFormat(DATE_FORMAT);

                    return (
                        <AccordionItem key={newsletter.key} className='rounded-md' value={newsletter.key}>
                            <AccordionTrigger
                                className='opacity-[.8]'
                                open={openItems.includes(newsletter.key)}
                            >
                                <div className='flex items-center gap-2'>
                                    <Icon icon={ICONS.DOCUMENTATION} />
                                    <Entity
                                        entity={isFirst ? 'new_delivery' : 'update_date'}
                                        namespace={nsNewsletter}
                                        data={{ date: formattedDate }}
                                    />
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className='text-base [&> img]:mt-4'>
                                <newsletter.Component params={baseNewsletterProps} />
                            </AccordionContent>
                        </AccordionItem>
                    );
                })}
            </Accordion>
        </div >
    );
}

export default NewsLetterContent;
